import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { SHOW_RENT, HIDDEN_PROPERTY_TYPES } from "@/lib/featureFlags";

const CITIES = ["All", "Indore", "Goa"];
const TYPES = ["All", "Residential", "Commercial", "Plot", "Airbnb"].filter(
  (x) => !HIDDEN_PROPERTY_TYPES.includes(x)
);
const BUY_PRICE_RANGES = [
  { label: "Any", min: null, max: null },
  { label: "Under ₹50L", min: 0, max: 5000000 },
  { label: "₹50L — ₹1Cr", min: 5000000, max: 10000000 },
  { label: "₹1Cr — ₹3Cr", min: 10000000, max: 30000000 },
  { label: "₹3Cr+", min: 30000000, max: null },
];

const RENT_PRICE_RANGES = [
  { label: "Any", min: null, max: null },
  { label: "Up to ₹10,000/mo", min: 0, max: 10000 },
  { label: "₹10,000 — ₹25,000/mo", min: 10000, max: 25000 },
  { label: "₹25,000 — ₹50,000/mo", min: 25000, max: 50000 },
  { label: "₹50,000 — ₹1 Lac/mo", min: 50000, max: 100000 },
  { label: "₹1 Lac — ₹2 Lac/mo", min: 100000, max: 200000 },
  { label: "₹2 Lac — ₹5 Lac/mo", min: 200000, max: 500000 },
  { label: "₹5 Lac — ₹10 Lac/mo", min: 500000, max: 1000000 },
  { label: "₹10 Lac — ₹20 Lac/mo", min: 1000000, max: 2000000 },
  { label: "₹20 Lac+/mo", min: 2000000, max: null },
];

export default function SearchBar({ compact = false, defaults = {} }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [listingType, setListingType] = useState(
    SHOW_RENT ? defaults.listing_type || "buy" : "buy"
  );
  const [city, setCity] = useState(defaults.city || "All");
  const [ptype, setPtype] = useState(
    TYPES.includes(defaults.property_type) ? defaults.property_type : "All"
  );
  const [price, setPrice] = useState("0");

  const priceRanges = listingType === "rent" ? RENT_PRICE_RANGES : BUY_PRICE_RANGES;

  const changeListingType = (next) => {
    setListingType(next);
    setPrice("0"); // reset to "Any" so bucket index can't carry across the mismatched array
  };

  const submit = () => {
    const params = new URLSearchParams();
    if (listingType) params.set("listing_type", listingType);
    if (city && city !== "All") params.set("city", city);
    if (ptype && ptype !== "All") params.set("property_type", ptype);
    const pr = priceRanges[Number(price)];
    if (pr?.min != null) params.set("min_price", pr.min);
    if (pr?.max != null) params.set("max_price", pr.max);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div
      data-testid="search-bar"
      className={`bg-white rounded-2xl ${
        compact ? "p-4" : "p-6 md:p-7"
      } shadow-[0_30px_60px_-30px_rgba(11,37,69,0.45)] border border-[#EBEFF5]`}
    >
      {/* Buy/Rent tabs */}
      <div className="inline-flex p-1 rounded-full bg-[#F4F7FB] mb-5">
        <button
          data-testid="search-buy-tab"
          onClick={() => changeListingType("buy")}
          className={`px-5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors ${
            listingType === "buy"
              ? "bg-[#0B2545] text-[#C9A84C]"
              : "text-[#1F2A44] hover:text-[#0B2545]"
          }`}
        >
          {t.search.buy}
        </button>
        {SHOW_RENT && (
        <button
          data-testid="search-rent-tab"
          onClick={() => changeListingType("rent")}
          className={`px-5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors ${
            listingType === "rent"
              ? "bg-[#0B2545] text-[#C9A84C]"
              : "text-[#1F2A44] hover:text-[#0B2545]"
          }`}
        >
          {t.search.rent}
        </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#334155] mb-1.5">
            {t.search.city}
          </label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger
              data-testid="city-select-dropdown"
              className="h-11 border-[#EBEFF5] bg-white text-[#0B2545]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`city-option-${c}`}>
                  {c === "All"
                    ? t.cities.all
                    : c === "Indore"
                    ? t.cities.indore
                    : c === "Goa"
                    ? t.cities.goa
                    : c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#334155] mb-1.5">
            {t.search.type}
          </label>
          <Select value={ptype} onValueChange={setPtype}>
            <SelectTrigger
              data-testid="search-property-type"
              className="h-11 border-[#EBEFF5] bg-white text-[#0B2545]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((tp) => (
                <SelectItem key={tp} value={tp} data-testid={`type-option-${tp}`}>
                  {tp === "All" ? t.types.all : t.types[tp] || tp}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#334155] mb-1.5">
            {t.search.price_range}
          </label>
          <Select value={price} onValueChange={setPrice}>
            <SelectTrigger
              data-testid="search-price-select"
              className="h-11 border-[#EBEFF5] bg-white text-[#0B2545]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priceRanges.map((pr, i) => (
                <SelectItem key={pr.label} value={String(i)} data-testid={`price-option-${i}`}>
                  {pr.label === "Any" ? t.search.any : pr.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end">
          <Button
            onClick={submit}
            data-testid="search-submit-btn"
            className="h-11 w-full bg-[#C9A84C] text-[#0B2545] hover:bg-[#DFBF65] font-bold tracking-wider uppercase text-xs"
          >
            <Search size={16} className="mr-2" />
            {t.search.search}
          </Button>
        </div>
      </div>
      {lang === "hi" && (
        <p className="mt-3 text-[11px] text-[#334155]">
          सभी भाषा लेबल दायें ऊपर के टॉगल बटन से बदलें।
        </p>
      )}
    </div>
  );
}
